/**
 * Recurrence Utilities
 *
 * Handles recurrence detection, occurrence generation, and display text
 * for recurring items across all item types.
 */

import {
  addDays,
  subDays,
  startOfDay,
  isAfter,
  isBefore,
  isEqual,
  isSameDay,
  getDay,
  getDate,
  getMonth,
  format,
} from 'date-fns';
import type { Item, SubItem, RecurrenceType, Occurrence, ItemType } from '../types';
import { getDefaultOccurrenceStatus } from '../types';

// ============================================================================
// CONSTANTS
// ============================================================================

const WINDOW_DAYS = 90;
const HORIZON_DAYS = 90;

// ============================================================================
// TYPE HELPERS
// ============================================================================

type RecurringItem = Item | SubItem;

interface RecurrenceFields {
  recurrence?: RecurrenceType;
  startDate?: { toDate: () => Date };
  endDate?: { toDate: () => Date };
  endDateForever?: boolean;
  daysOfWeek?: number[];
  daysOfMonth?: (number | string)[];
  datesOfYear?: string[];
  occurrences?: Occurrence[];
}

/**
 * Parse a date value that could be:
 * - A plain JavaScript Date
 * - A Firestore Timestamp (has toDate() method)
 * - A serialized date string/number
 */
function parseOccurrenceDate(dateValue: unknown): Date {
  if (dateValue instanceof Date) {
    return dateValue;
  }
  // Handle Firestore Timestamp (has toDate method)
  if (dateValue && typeof (dateValue as { toDate?: () => Date }).toDate === 'function') {
    return (dateValue as { toDate: () => Date }).toDate();
  }
  // Handle serialized date (string or number)
  return new Date(dateValue as string | number);
}

// ============================================================================
// RECURRENCE DETECTION
// ============================================================================

/**
 * Check if an item is recurring (has recurrence !== 'none')
 */
export function isRecurringItem(item: RecurringItem): boolean {
  const recurrence = (item as unknown as RecurrenceFields).recurrence;
  return recurrence !== undefined && recurrence !== 'none';
}

/**
 * Get human-readable label for recurrence type
 */
export function getRecurrenceLabel(recurrence: RecurrenceType | undefined): string {
  if (!recurrence) return 'None';
  const labels: Record<RecurrenceType, string> = {
    none: 'None',
    daily: 'Daily',
    weekly: 'Weekly',
    monthly: 'Monthly',
    yearly: 'Yearly',
  };
  return labels[recurrence] || 'None';
}

// ============================================================================
// OCCURRENCE GENERATION
// ============================================================================

/**
 * Generate occurrences for a recurring item within a date range.
 * Used by itemService on create/edit and by cloud function for extension.
 */
export function generateOccurrences(
  item: RecurringItem,
  rangeStart: Date,
  rangeEnd: Date
): Occurrence[] {
  const fields = item as unknown as RecurrenceFields;
  const recurrence = fields.recurrence;

  if (!recurrence || recurrence === 'none') {
    return [];
  }

  const startDate = fields.startDate?.toDate();
  if (!startDate) {
    return [];
  }

  const endDate = fields.endDateForever ? null : fields.endDate?.toDate();
  const occurrences: Occurrence[] = [];

  // Determine actual range considering item's start/end dates
  // Normalize startDate to midnight to avoid time-component issues
  const normalizedStartDate = startOfDay(startDate);
  const effectiveStart = isAfter(rangeStart, normalizedStartDate) ? rangeStart : normalizedStartDate;
  const effectiveEnd = endDate && isBefore(endDate, rangeEnd) ? endDate : rangeEnd;

  let currentDate: Date | null = startOfDay(startDate);

  // Advance to effectiveStart
  while (currentDate && isBefore(currentDate, effectiveStart)) {
    currentDate = getNextOccurrenceDate(currentDate, recurrence, fields);
  }

  // Generate occurrences within range
  while (currentDate && (isBefore(currentDate, effectiveEnd) || isEqual(currentDate, effectiveEnd))) {
    if (matchesRecurrencePattern(currentDate, recurrence, fields)) {
      occurrences.push({
        date: new Date(currentDate),
        status: getDefaultOccurrenceStatus(item.type as ItemType),
      });
    }
    currentDate = getNextOccurrenceDate(currentDate, recurrence, fields);
  }

  return occurrences;
}

/**
 * Get the next occurrence date based on recurrence type
 */
function getNextOccurrenceDate(
  currentDate: Date,
  recurrence: RecurrenceType,
  _fields: RecurrenceFields
): Date | null {
  switch (recurrence) {
    case 'daily':
      return addDays(currentDate, 1);
    case 'weekly':
      return addDays(currentDate, 1); // Check each day for daysOfWeek
    case 'monthly':
      return addDays(currentDate, 1); // Check each day for daysOfMonth
    case 'yearly':
      return addDays(currentDate, 1); // Check each day for datesOfYear
    default:
      return null;
  }
}

/**
 * Check if a date matches the recurrence pattern
 */
function matchesRecurrencePattern(
  date: Date,
  recurrence: RecurrenceType,
  fields: RecurrenceFields
): boolean {
  switch (recurrence) {
    case 'daily':
      return true;

    case 'weekly': {
      const daysOfWeek = fields.daysOfWeek;
      if (!daysOfWeek || daysOfWeek.length === 0) {
        // Default to same day as start date
        const startDay = fields.startDate?.toDate();
        return startDay ? getDay(date) === getDay(startDay) : false;
      }
      return daysOfWeek.includes(getDay(date));
    }

    case 'monthly': {
      const daysOfMonth = fields.daysOfMonth;
      if (!daysOfMonth || daysOfMonth.length === 0) {
        // Default to same day of month as start date
        const startDate = fields.startDate?.toDate();
        return startDate ? getDate(date) === getDate(startDate) : false;
      }
      const dayOfMonth = getDate(date);
      // Handle numeric days and 'last'
      return daysOfMonth.some((d) => {
        if (d === 'last') {
          const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
          return dayOfMonth === lastDay;
        }
        if (typeof d === 'number') {
          return dayOfMonth === d;
        }
        // Handle ordinal patterns like "First Sat", "Second Sun"
        return matchesOrdinalPattern(date, d as string);
      });
    }

    case 'yearly': {
      const datesOfYear = fields.datesOfYear;
      if (!datesOfYear || datesOfYear.length === 0) {
        // Default to same month/day as start date
        const startDate = fields.startDate?.toDate();
        return startDate
          ? getMonth(date) === getMonth(startDate) && getDate(date) === getDate(startDate)
          : false;
      }
      const mmdd = format(date, 'MM-dd');
      return datesOfYear.includes(mmdd);
    }

    default:
      return false;
  }
}

/**
 * Match ordinal patterns like "First Sat", "Second Sun", "Last Fri"
 */
function matchesOrdinalPattern(date: Date, pattern: string): boolean {
  const parts = pattern.toLowerCase().split(' ');
  if (parts.length !== 2) return false;

  const ordinal = parts[0];
  const dayName = parts[1];

  const dayMap: Record<string, number> = {
    sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6,
  };

  const targetDay = dayMap[dayName];
  if (targetDay === undefined) return false;

  if (getDay(date) !== targetDay) return false;

  const dayOfMonth = getDate(date);
  const weekOfMonth = Math.ceil(dayOfMonth / 7);

  switch (ordinal) {
    case 'first':
      return weekOfMonth === 1;
    case 'second':
      return weekOfMonth === 2;
    case 'third':
      return weekOfMonth === 3;
    case 'fourth':
      return weekOfMonth === 4;
    case 'last': {
      // Check if this is the last occurrence of this day in the month
      const nextWeek = addDays(date, 7);
      return nextWeek.getMonth() !== date.getMonth();
    }
    default:
      return false;
  }
}

// ============================================================================
// OCCURRENCE STATISTICS
// ============================================================================

/**
 * Get stats for past occurrences (last 90 days)
 */
export function getPastDueStats(item: RecurringItem): { complete: number; overdue: number } {
  const fields = item as unknown as RecurrenceFields;
  const occurrences = fields.occurrences || [];
  const now = startOfDay(new Date());
  const rangeStart = subDays(now, WINDOW_DAYS);

  let complete = 0;
  let overdue = 0;

  for (const occ of occurrences) {
    const occDate = parseOccurrenceDate(occ.date);
    if (isAfter(occDate, rangeStart) && isBefore(occDate, now)) {
      if (occ.status === 'complete') {
        complete++;
      } else {
        overdue++;
      }
    }
  }

  return { complete, overdue };
}

/**
 * Get count of upcoming occurrences (next 90 days)
 */
export function getUpcomingCount(item: RecurringItem): number {
  const fields = item as unknown as RecurrenceFields;
  const occurrences = fields.occurrences || [];
  const now = startOfDay(new Date());
  const rangeEnd = addDays(now, WINDOW_DAYS);

  let count = 0;
  for (const occ of occurrences) {
    const occDate = parseOccurrenceDate(occ.date);
    if ((isAfter(occDate, now) || isEqual(occDate, now)) && isBefore(occDate, rangeEnd)) {
      count++;
    }
  }

  return count;
}

/**
 * Check if item has occurrences in past 90 days
 */
export function hasOccurrencesInPast90Days(item: RecurringItem): boolean {
  const { complete, overdue } = getPastDueStats(item);
  return complete + overdue > 0;
}

/**
 * Check if item has occurrences in next 90 days
 */
export function hasOccurrencesInNext90Days(item: RecurringItem): boolean {
  return getUpcomingCount(item) > 0;
}

/**
 * Check if item has an occurrence today
 */
export function hasOccurrenceToday(item: RecurringItem): boolean {
  const fields = item as unknown as RecurrenceFields;
  const occurrences = fields.occurrences || [];
  const today = startOfDay(new Date());

  return occurrences.some((occ) => {
    const occDate = parseOccurrenceDate(occ.date);
    return isSameDay(occDate, today);
  });
}

// ============================================================================
// DISPLAY TEXT
// ============================================================================

// New tab structure for recurring support
type TabKey = 'allScheduled' | 'allUnscheduled' | 'allRecurring' | 'pastWeek' | 'today' | 'nextWeek';

/**
 * Get display text for Row 4 based on current tab context
 */
export function getRecurrenceDisplayText(
  item: RecurringItem,
  currentTab: TabKey | undefined
): string {
  const fields = item as unknown as RecurrenceFields;
  const recurrence = fields.recurrence || 'none';
  const label = getRecurrenceLabel(recurrence);

  switch (currentTab) {
    case 'today':
      return `Due Date: Today (Recurring: ${label})`;

    case 'allScheduled':
    case 'allRecurring':
      return `Recurring: ${label}`;

    case 'pastWeek': {
      const { complete, overdue } = getPastDueStats(item);
      return `${complete} complete, ${overdue} overdue (last 7 days)`;
    }

    case 'nextWeek': {
      const count = getUpcomingCount(item);
      return `${count} upcoming in 7 days`;
    }

    default:
      return `Recurring: ${label}`;
  }
}

// ============================================================================
// INITIAL OCCURRENCE GENERATION (For itemService)
// ============================================================================

/**
 * Generate initial occurrences for a new recurring item (180-day horizon)
 */
export function generateInitialOccurrences(item: RecurringItem): Occurrence[] {
  const fields = item as unknown as RecurrenceFields;
  if (!isRecurringItem(item)) {
    return [];
  }

  const startDate = fields.startDate?.toDate();
  if (!startDate) {
    return [];
  }

  const rangeStart = startOfDay(startDate);
  const rangeEnd = addDays(new Date(), HORIZON_DAYS);

  return generateOccurrences(item, rangeStart, rangeEnd);
}

/**
 * Update occurrences for an edited recurring item.
 * Preserves past occurrences, backfills missing past dates, regenerates future ones.
 */
export function updateOccurrencesOnEdit(
  item: RecurringItem,
  existingOccurrences: Occurrence[]
): Occurrence[] {
  const today = startOfDay(new Date());
  const fields = item as unknown as RecurrenceFields;
  const startDate = fields.startDate?.toDate();

  // Keep existing past occurrences (immutable history)
  const existingPastOccurrences = existingOccurrences.filter((occ) => {
    const occDate = parseOccurrenceDate(occ.date);
    return isBefore(occDate, today);
  });

  // Build set of existing past occurrence dates for quick lookup
  const existingPastDates = new Set(
    existingPastOccurrences.map((occ) => startOfDay(parseOccurrenceDate(occ.date)).getTime())
  );

  // Backfill missing past occurrences if startDate is before today
  let backfilledOccurrences: Occurrence[] = [];
  if (startDate && isBefore(startOfDay(startDate), today)) {
    const yesterday = subDays(today, 1);
    const potentialPastOccurrences = generateOccurrences(item, startOfDay(startDate), yesterday);

    // Only add occurrences for dates that don't already exist
    backfilledOccurrences = potentialPastOccurrences.filter((occ) => {
      const occTime = startOfDay(parseOccurrenceDate(occ.date)).getTime();
      return !existingPastDates.has(occTime);
    });
  }

  // Regenerate future occurrences
  const rangeEnd = addDays(today, HORIZON_DAYS);
  const futureOccurrences = generateOccurrences(item, today, rangeEnd);

  return [...existingPastOccurrences, ...backfilledOccurrences, ...futureOccurrences];
}

// ============================================================================
// OCCURRENCE EXTENSION (For on-demand generation beyond horizon)
// ============================================================================

/**
 * Extend occurrences for a recurring item beyond the current horizon.
 * Used when user navigates calendar beyond 180 days.
 *
 * @param item - The recurring item
 * @param existingOccurrences - Current occurrences array
 * @param targetDate - The date user is navigating to
 * @returns Extended occurrences array (merged with existing, no duplicates)
 */
export function extendOccurrences(
  item: RecurringItem,
  existingOccurrences: Occurrence[],
  targetDate: Date
): Occurrence[] {
  if (!isRecurringItem(item)) {
    return existingOccurrences;
  }

  // Find the furthest existing occurrence date
  let furthestDate = startOfDay(new Date());
  for (const occ of existingOccurrences) {
    const occDate = parseOccurrenceDate(occ.date);
    if (isAfter(occDate, furthestDate)) {
      furthestDate = occDate;
    }
  }

  // If target is already within existing occurrences, no extension needed
  const targetEnd = addDays(startOfDay(targetDate), HORIZON_DAYS);
  if (isBefore(targetEnd, furthestDate) || isEqual(targetEnd, furthestDate)) {
    return existingOccurrences;
  }

  // Generate new occurrences from furthest existing to target + horizon
  const rangeStart = addDays(furthestDate, 1); // Day after furthest existing
  const newOccurrences = generateOccurrences(item, rangeStart, targetEnd);

  // Merge and return (existing + new)
  return [...existingOccurrences, ...newOccurrences];
}

/**
 * Check if a date is beyond the current occurrence horizon.
 * Returns true if occurrences need to be extended to display this date.
 */
export function needsOccurrenceExtension(
  existingOccurrences: Occurrence[],
  targetDate: Date
): boolean {
  if (existingOccurrences.length === 0) {
    return false; // No occurrences = not a recurring item or no extension needed
  }

  // Find the furthest existing occurrence date
  let furthestDate = startOfDay(new Date());
  for (const occ of existingOccurrences) {
    const occDate = parseOccurrenceDate(occ.date);
    if (isAfter(occDate, furthestDate)) {
      furthestDate = occDate;
    }
  }

  // Target is beyond if it's after the furthest occurrence
  return isAfter(startOfDay(targetDate), furthestDate);
}
